import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import {
  isBoolean,
  isEqual,
  isNil,
  toUpper,
  toString,
  isString,
  isEmpty,
} from "lodash";
import { v4 as uuid } from "uuid";

import { resolveFieldAgainstShadowStatus } from "../../../util/UtilForm";
import CustomFileViewer from "../fileChooserDialog/CustomFileViewer";
import { normalizeFileTypes } from "../fileChooserDialog/formats";
import { useTranslation } from "react-i18next";
import { useRequestHeaders } from "../../../core/hooks/useRequestHeaders";
import { getPresignedURLOnDemand } from "../fileChooserDialog/api";
import { toast } from "react-toastify";
import { TOAST_CONTAINER_LAYOUT } from "../../../util/Constants";

const commonIcon = {
  width: "auto",
  height: "auto",
  padding: 1,
  fontSize: 16,
};

const useStyles = makeStyles((theme) => ({
  baseBtn: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    marginInline: 6,
    textTransform: "none",
  },
  dialogTitle: {
    textAlign: "center",
    background: "#f7f9fb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 3),
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(1.05)",
    },
    "100%": {
      transform: "scale(1)",
    },
  },
  "@keyframes glitch": {
    "0%": {
      transform: "translate(0)",
    },
    "20%": {
      transform: "translate(-2px, 2px)",
    },
    "60%": {
      transform: "translate(2px, 2px)",
    },
    "100%": {
      transform: "translate(0)",
    },
  },
  "@keyframes bounce": {
    "0%, 100%": {
      transform: "translateY(0)",
    },
    "50%": {
      transform: "translateY(-2px)",
    },
  },
  pulse: {
    animation: "$pulse 1s ease-in-out infinite",
  },
  glitch: {
    animation: "$glitch 3s ease-in-out infinite",
  },
  bounce: {
    animation: "$bounce 1s ease-in-out infinite",
  },
}));

export function needToShowAdditionalAction({ aditionalAction, shadowStatus }) {
  if (isNil(aditionalAction)) {
    return false;
  }

  if (isNil(aditionalAction?.showCondition)) {
    return false;
  }

  const showCondition = aditionalAction?.showCondition;

  if (isString(showCondition)) {
    const valueInShadowStatus = resolveFieldAgainstShadowStatus({
      field: showCondition,
      shadowStatus,
      completeIdControl: showCondition,
    });
    return (
      isEqual("S", toUpper(toString(valueInShadowStatus))) ||
      isEqual("TRUE", toUpper(toString(valueInShadowStatus))) ||
      isEqual("Y", toUpper(toString(valueInShadowStatus)))
    );
  } else if (isBoolean(showCondition)) {
    return showCondition;
  } else {
    return false;
  }
}

export default function AdditionalActionsItem(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const REQUEST_HEADERS = useRequestHeaders();

  const { aditionalAction, shadowStatus } = props;

  const needToShow = needToShowAdditionalAction({
    aditionalAction,
    shadowStatus,
  });

  // styles and animations
  const icon = aditionalAction?.icon;
  const colorText = aditionalAction?.colorText;
  const colorBg = aditionalAction?.colorBg;
  const animation = aditionalAction?.animation;

  // information
  const label = aditionalAction?.label;
  const tooltip = aditionalAction?.tooltip;
  const tooltipPosition = aditionalAction?.tooltipPosition;
  const titleContent = aditionalAction?.titleContent;

  // behavior
  const keyContent = aditionalAction?.keyContent;
  //const isVideo = toString(keyContent).toUpperCase().endsWith("MP4");

  useEffect(() => {
    async function obtainUrl() {
      if (loading) {
        return;
      }
      const toastId = "obtainUrl";
      try {
        setLoading(true);
        const response = await getPresignedURLOnDemand({
          REQUEST_HEADERS,
          fileKey: keyContent,
        });

        if (!response.ok) {
          throw new Error(response.errorMsg || "Error fetching file URL");
        }

        setFileUrl(response.dataResponse);
      } catch (error) {
        toast.error(error.message || t("ERROR_FETCHING_FILE"), {
          toastId,
          containerId: TOAST_CONTAINER_LAYOUT,
        });
        setShowDialog(false);
      } finally {
        setLoading(false);
      }
    }

    if (!isNil(keyContent) && showDialog && isNil(fileUrl)) {
      obtainUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [REQUEST_HEADERS, keyContent, t]);

  const handleClose = () => {
    setShowDialog(false);
    setFileUrl(null);
  };

  if (needToShow === false) {
    return false;
  }

  return (
    <>
      <Tooltip
        title={tooltip}
        placement={tooltipPosition}
        disableHoverListener={isNil(tooltip) || isEmpty(tooltip)}
      >
        <Button
          className={`${classes.baseBtn} ${
            animation ? classes[animation] : ""
          }`}
          size="small"
          startIcon={
            !isNil(icon) ? <Icon className={icon} style={commonIcon} /> : null
          }
          style={{
            ...(colorText ? { color: colorText } : {}),
            ...(colorBg ? { backgroundColor: colorBg } : {}),
          }}
          onClick={() => setShowDialog(true)}
        >
          {label}
        </Button>
      </Tooltip>
      {!isNil(keyContent) ? (
        <Dialog
          fullWidth={true}
          maxWidth={"lg"}
          //fullScreen={isVideo}
          open={showDialog}
          aria-labelledby="max-width-dialog-title"
        >
          <DialogTitle disableTypography className={classes.dialogTitle}>
            <Typography variant="h4">{`${titleContent || "File"}`}</Typography>
            <Tooltip title={t("CLOSE")} placement="left">
              <IconButton onClick={handleClose} aria-label="close">
                <Icon
                  className="fas fa-times-circle"
                  style={{ ...commonIcon, fontSize: "1.5rem" }}
                />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "2rem",
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              fileUrl && (
                <CustomFileViewer
                  id={keyContent}
                  keyViewer={`${uuid()}_aa_viewer`}
                  fileType={normalizeFileTypes({
                    baseFileType: keyContent,
                  })}
                  filePath={fileUrl}
                  disableFullScreen={true}
                />
              )
            )}
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
